import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';

import { timeoutFetchSentryGuard } from 'Fetch';
import cypressIds from 'CypressId';

import { ConfigContext } from 'Context/config';

import { applyCypressData } from 'Utilities/index';
import { GUIDES_PATH } from 'Utilities/wordpress';
import { WordpressGuideSummaryData } from 'Utilities/wordpress/@types';
import { getGuidesPreviewData } from 'Utilities/wordpressPages';

import { useBreakpoint } from '../../../../../components/misc/Breakpoints/Breakpoints';
import { usePageRefEvents } from '../../../common/Hooks/usePageRefEvents';
import { RoundedSection, RoundedSectionType } from '../../../common/RoundedSection/RoundedSection';

import { GuideCarousel } from './subComponents/GuideCarousel';
import { GuideList } from './subComponents/GuideList';
import GuidesTexts from './Guides.texts.json';
import { GuideData } from './Guides.types';
import { getGuidesData } from './guidesHelper';

import styles from './Guides.scss';

const GuidesT = new MDText(GuidesTexts);

const t = (key: string, args?: Record<string, string>) => `${GuidesT.translate(key, args)}`;

const texts = {
  cta: t('cta'),
  details: t('details'),
  title: t('title'),
} as const;

type GuidesProps = {
  guides?: readonly WordpressGuideSummaryData[];
  onCtaClick?: (path: string) => void;
  onGuideClick?: (guide: GuideData) => void;
  onLoad?: (label: string) => void;
  theme?: RoundedSectionType;
  title?: string;
  titleClassName?: string;
  wrapperClassName?: string;
};

export const Guides = ({
  guides: providedGuides,
  onCtaClick,
  onGuideClick,
  onLoad,
  theme = 'primary-yellow',
  title,
  titleClassName,
  wrapperClassName,
}: GuidesProps) => {
  const { configState: { guidesPreviewData } } = useContext(ConfigContext);
  const hasProvidedGuides = Boolean(providedGuides?.length);
  const [fetchedGuidesData, setFetchedGuidesData] = useState(guidesPreviewData);
  const { minWidth: breakpoints } = useBreakpoint();
  const { breakpointContent } = breakpoints;

  useEffect(() => {
    const loadGuidesData = async () => {
      if (process.env.NODE_ENV === 'production') {
        try {
          return setFetchedGuidesData(await getGuidesPreviewData());
        } catch (err) {
          timeoutFetchSentryGuard(err, () => {
            window?.Sentry?.captureException?.(new Error((err instanceof Error) ? err.message : 'failed to fetch guide data'), {
              level: 'warning',
              tags: {
                fetch: 'guides',
              },
            });
          });
        }
      }

      return import('./mockGuidePreviewData').then(({ mockGuidesPreviewData }) => setFetchedGuidesData(mockGuidesPreviewData));
    };

    if (!fetchedGuidesData && !hasProvidedGuides) {
      loadGuidesData().catch(() => {});
    }
  }, [fetchedGuidesData, hasProvidedGuides]);

  const sourceData = hasProvidedGuides ? providedGuides : fetchedGuidesData;
  const guides = sourceData && getGuidesData(sourceData);
  const eventLabels = (guides ?? []).map(({ title: guideTitle }) => guideTitle);

  const {
    $wrapper,
    entryMap: eventEntryMap,
    isSectionVisible,
    setRef: setGuideRef,
    update: updateRefEvents,
  } = usePageRefEvents({
    eventLabels,
    onRefTrigger: (label) => label && onLoad?.(label),
  });

  const GuidesComponent = !breakpointContent && isSectionVisible ? GuideCarousel : GuideList;

  useEffect(() => {
    updateRefEvents();
  }, [GuidesComponent, updateRefEvents]);

  if (!guides) {
    return null;
  }

  return <RoundedSection withBottomLedge className={styles.guidesSection} theme={theme}>
    <div
      ref={$wrapper}
      className={cx(styles.container, wrapperClassName)}
      {...applyCypressData(cypressIds.sections.guides)}
    >
      <div className={styles.caption}>
        <h2 className={cx(styles.title, titleClassName)}>{title || texts.title}</h2>
        <p className={cx(styles.details, 'mw-i')}>
          {texts.details}
        </p>
      </div>
      <div className={styles.ctaWrapper}>
        <Button
          as={Link}
          icon="chevron"
          label={texts.cta}
          onClick={() => onCtaClick?.(GUIDES_PATH)}
          to={GUIDES_PATH}
          variant="secondary"
        />
      </div>
      <GuidesComponent className={styles.guides} {...{ guides, onGuideClick }} setGuideRef={(ref, index) => {
        const entry = eventEntryMap.get(eventLabels[index]);
        if (!entry?.isIntersecting) {
          setGuideRef(ref, index);
        }
      }}/>
    </div>
  </RoundedSection>;
};
